import { generateTestRunId, parseError, parseNestedSteps, replacer } from './helpers';
import { buildAxiosConfig } from './helpers/axios-config.js';
import {
  ExtendIntegrationScenarioPayload,
  ExtendIntegrationScenarioPayloadSchema,
} from './types/wopeeReporter.interface.js';
import type { Reporter, TestCase, TestResult } from '@playwright/test/reporter';
import axios from 'axios';
import 'dotenv/config';
import fs from 'node:fs';

export class WopeeReporter implements Reporter {
  private uploadQueue: Promise<any>[];
  private suiteUuids: Set<string>;
  private configWarned: boolean;

  constructor() {
    this.uploadQueue = [];
    this.suiteUuids = new Set();
    this.configWarned = false;
  }

  /*
  Read lazily rather than at module load, so a playwright.config.ts that assigns
  process.env.WOPEE_* at runtime is picked up.
  */
  private env() {
    const { WOPEE_API_KEY, WOPEE_API_URL, WOPEE_PROJECT_UUID } = process.env;
    return { WOPEE_API_KEY, WOPEE_API_URL, WOPEE_PROJECT_UUID };
  }

  /*
  A reporter must never fail the run, so this warns and skips rather than
  throwing — otherwise an unauthenticated request is queued and its 401 is
  swallowed by onExit, leaving the user with silently missing results.
  */
  private hasCredentials(): boolean {
    const { WOPEE_API_KEY, WOPEE_PROJECT_UUID } = this.env();
    const missing = [
      ...(WOPEE_API_KEY?.trim() ? [] : ['WOPEE_API_KEY']),
      ...(WOPEE_PROJECT_UUID?.trim() ? [] : ['WOPEE_PROJECT_UUID']),
    ];

    if (missing.length === 0) {
      return true;
    }

    if (!this.configWarned) {
      this.configWarned = true;
      console.error(
        `[WOPEE_REPORTER_ERROR]: Wopee reporter is disabled — ${missing.join(' and ')} ${
          missing.length > 1 ? 'are' : 'is'
        } not set. Get credentials from Project Settings → API Keys at https://cmd.wopee.io and set them in your shell, a .env file, or CI secrets. Test results will not be uploaded.`,
      );
    }
    return false;
  }

  onTestEnd(test: TestCase, result: TestResult) {
    const testRunId = generateTestRunId();

    const wopeeSuiteUuid = result.attachments.find((a) => a?.name === 'wopee-suite-uuid')?.body?.toString('utf-8');
    if (wopeeSuiteUuid) {
      this.suiteUuids.add(wopeeSuiteUuid);
    }

    const wopeeScenarioUuid = result.attachments
      .find((a) => a?.name === 'wopee-scenario-uuid')
      ?.body?.toString('utf-8');

    const failedStep = [...result.steps].reverse().find((step) => step.error);

    const createStepId = (step: any) => `${step.title}-${step.category}-${step.startTime}`;
    const failedStepId = failedStep ? createStepId(failedStep) : null;

    let screenshotBase64: string | null = null;
    let videoBase64: string | null = null;

    for (const attachment of result.attachments) {
      if (attachment.name === 'screenshot' && attachment.contentType === 'image/png') {
        const filePath = attachment.path;
        if (filePath) {
          try {
            const imageBuffer = fs.readFileSync(filePath);
            screenshotBase64 = imageBuffer.toString('base64');
          } catch (error) {
            console.error('Error reading screenshot:', error);
          }
        }
      }

      if (
        attachment.name === 'video' &&
        (attachment.contentType === 'video/webm' ||
          attachment.contentType === 'video/mp4' ||
          attachment.contentType === 'video/avi')
      ) {
        const filePath = attachment.path;
        if (filePath) {
          try {
            const videoBuffer = fs.readFileSync(filePath);
            videoBase64 = videoBuffer.toString('base64');
          } catch (error) {
            console.error('Error reading video:', error);
          }
        }
      }
    }

    if (!wopeeScenarioUuid) {
      return console.error(
        '[WOPEE_REPORTER_ERROR]: Scenario UUID is required, please propagate "testInfo" as second argument in "test.beforeEach" hook and pass it to "wopee.startScenario("scenarioTitle", testInfo)" method as second argument if you want to use features of Wopee reporter',
      );
    }

    if (!this.hasCredentials()) {
      return;
    }

    const steps = result.steps.map((step, index) => ({
      testRunId,
      number: index + 1,
      testTitle: test.title,
      title: step.title,
      status: step.error ? 'failed' : 'passed',
      error: step.error ? parseError(step.error) : step.error,
      category: step.category,
      location: step.location,
      duration: step.duration,
      startTime: step.startTime,
      steps: parseNestedSteps(step.steps),
      screenshotBase64: createStepId(step) === failedStepId ? screenshotBase64 : null,
      videoBase64: createStepId(step) === failedStepId ? videoBase64 : null,
    }));

    const query = `
    mutation ExtendIntegrationScenario($input: ExtendIntegrationScenarioInput!) {
      extendIntegrationScenario(input: $input)
    }`;

    const extendIntegrationScenarioPayload: ExtendIntegrationScenarioPayload = {
      input: {
        projectUuid: this.env().WOPEE_PROJECT_UUID,
        scenarioUuid: wopeeScenarioUuid,
        pwSteps: JSON.stringify(steps, replacer),
        ...(process.env.GITHUB_RUN_ID && {
          githubRunId: process.env.GITHUB_RUN_ID,
        }),
        ...(process.env.GITHUB_RUN_ATTEMPT && {
          githubRunAttempt: Number(process.env.GITHUB_RUN_ATTEMPT),
        }),
      },
    };
    const variables = ExtendIntegrationScenarioPayloadSchema.parse(extendIntegrationScenarioPayload);

    const uploadPromise = new Promise(async (resolve, reject) => {
      try {
        const { apiUrl, config } = this.apiRequestConfig();
        const response = await axios.post(apiUrl, { query, variables }, config);
        resolve(response);
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const status = error.response?.status;
          const errorData = error.response?.data;
          const errorMessage = error.message;
          console.error('[WOPEE_REPORTER_ERROR]', errorMessage, errorData);
          return reject(new Error(`API request failed with status code: ${status}.`));
        }
        reject(new Error(`[WOPEE_REPORTER_ERROR] Unexpected error occurred during API call: ${error?.message}`));
      }
    });

    this.uploadQueue.push(uploadPromise);
  }

  async onExit(): Promise<void> {
    if (this.uploadQueue.length > 0) {
      console.log(`Uploading ${this.uploadQueue.length} remaining test results...`);
      try {
        await Promise.all(this.uploadQueue);
        console.log('Successfully uploaded all test results.');
      } catch (error) {
        console.error('Error uploading test results:', error);
      } finally {
        this.uploadQueue = [];
      }
    }

    await this.stopSuites();
  }

  private apiRequestConfig() {
    const { WOPEE_API_KEY, WOPEE_API_URL } = this.env();
    const apiUrl = WOPEE_API_URL || 'https://api.wopee.io';
    const headers = {
      api_key: WOPEE_API_KEY,
    };
    return { apiUrl, config: buildAxiosConfig(apiUrl, headers) };
  }

  // Teardown is best-effort: a failed stop must never fail the run — suites are also
  // finished lazily by the API when left IDLE.
  private async stopSuites(): Promise<void> {
    // Without credentials this would fire unauthenticated requests whose 401s
    // contradict the "results will not be uploaded" warning already logged.
    if (this.suiteUuids.size > 0 && !this.hasCredentials()) {
      this.suiteUuids.clear();
      return;
    }

    const query = `
    mutation StopIntegrationSuite($suiteUuid: ID!) {
      stopIntegrationSuite(suiteUuid: $suiteUuid) { uuid suiteRunningStatus }
    }`;

    for (const suiteUuid of this.suiteUuids) {
      try {
        const { apiUrl, config } = this.apiRequestConfig();
        const response = await axios.post(apiUrl, { query, variables: { suiteUuid } }, config);
        const errors = response.data?.errors;
        if (errors?.length) {
          console.error(`[WOPEE_REPORTER_ERROR] Failed to stop suite ${suiteUuid}:`, errors[0]?.message);
        } else {
          console.log(`Suite(uuid=${suiteUuid}) is stopped.`);
        }
      } catch (error: any) {
        console.error(`[WOPEE_REPORTER_ERROR] Failed to stop suite ${suiteUuid}: ${error?.message}`);
      }
    }
    this.suiteUuids.clear();
  }

  printsToStdio(): boolean {
    return false;
  }
}
